import React, { FC, ReactNode, memo, useLayoutEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
	Paper,
	TableRow,
	TableCell,
	TableContainer,
	TableBody,
	Table,
	LinearProgress,
	Skeleton,
} from "@mui/material";

// Placeholder rows while loading (like the invoices table): one per row of the selected page size.
const DEFAULT_SKELETON_ROWS = 10;
// Measured height of the loaded rows per page, so a page's placeholders match its rows (e.g. taller rows with
// thumbnails) from the next load on. Before that, the placeholders are built like the rows: same cell padding and a
// button-sized block in the actions column.
const rowHeights = new Map<string, number>();

interface MainTableProps {
	loading: boolean;
	paginationComponent: ReactNode;
	headerComponent:ReactNode;
	rowComponents:ReactNode;
	// Number of placeholder rows (the selected page size) and columns while loading.
	skeletonRows?: number;
	skeletonColumns?: number;
}

const MainTable:FC<MainTableProps> = (props) => {
	const {
		loading,
		paginationComponent,
		headerComponent,
		rowComponents,
		skeletonRows = DEFAULT_SKELETON_ROWS,
		skeletonColumns = 1,
	} = props;

	// Every load (first load, refresh, page change, filter) shows placeholder rows in place of the rows.
	const showSkeletons = loading;

	const { pathname } = useLocation();
	const bodyRef = useRef<HTMLTableSectionElement>(null);
	const [rowHeight, setRowHeight] = useState<number | undefined>(() => rowHeights.get(pathname));

	// Measure the loaded rows (the closed detail rows under each row have no height, so they are left out).
	useLayoutEffect(() => {
		if (showSkeletons || !bodyRef.current) return;
		const heights = Array.from(bodyRef.current.rows)
			.map(row => row.getBoundingClientRect().height)
			.filter(height => height > 1)
			.sort((a, b) => a - b);
		if (!heights.length) return;
		const median = Math.round(heights[Math.floor(heights.length / 2)]);
		rowHeights.set(pathname, median);
		setRowHeight(median);
	}, [showSkeletons, rowComponents, pathname]);

	const columns = Math.max(1, skeletonColumns);

	return (
		<TableContainer component={Paper} className="pb-grid">
			{/* Loading like the invoices table: a thin progress bar on top and placeholder rows (no big spinner). */}
			<div className="pb-grid-progress">{loading && <LinearProgress />}</div>
			<Table
				aria-label="collapsible table"
				style={{
					pointerEvents: loading ? "none" : "all",
				}}>
				{headerComponent}
				<TableBody ref={bodyRef} style={{ opacity: loading && !showSkeletons ? 0.6 : 1 }}>
					{showSkeletons
						? Array.from({ length: Math.max(1, skeletonRows) }).map((_, index) => (
							<TableRow key={"skeleton-" + index} className="pb-grid-skeleton-row" style={rowHeight ? { height: rowHeight } : undefined}>
								{Array.from({ length: columns }).map((__, column) => (
									<TableCell key={column}>
										{column === columns - 1 ? (
											<div className="pb-skeleton-actions">
												<Skeleton variant="rounded" width={40} height={40} />
												<Skeleton variant="rounded" width={40} height={40} />
											</div>
										) : (
											<Skeleton variant="text" height={20} width="80%" />
										)}
									</TableCell>
								))}
							</TableRow>
						))
						: rowComponents}
				</TableBody>
			</Table>
			<div className="pb-grid-progress pb-grid-progress--bottom">{loading && <LinearProgress />}</div>
			{paginationComponent}
		</TableContainer>
	);
};

export default memo(MainTable);
